/**
 * The manual-review table on `/admin/study`: one row per participant, the
 * `review` rows of the participant listing. Prolific pays STAGE4 by itself; the owner
 * reviews everyone else by hand and pays them for their time from here, so
 * the table shows time, never money. A row opens its event timeline, and,
 * for a participant who has finished, the button that withdraws them. A
 * withdrawn participant keeps their row, marked withdrawn.
 */

import { Fragment } from 'react'
import type { StudyAuditEvent, StudyReviewRow } from '@overcooked/shared'

export const REVIEW_HEADINGS = [
  'PID',
  'Submission',
  'Outcome',
  'Highest stage',
  'Code',
  'Started',
  'Finished',
  'Time in study',
  'Waiting for partner',
  'Test',
] as const

/** A duration as a clock: `m:ss` under an hour, `h:mm:ss` from an hour on. */
export function reviewDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const ss = String(total % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

const stamp = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—')

/** What an event carries besides its time and type, for the timeline. */
const detail = (e: StudyAuditEvent): string =>
  JSON.stringify(Object.fromEntries(Object.entries(e).filter(([k]) => !['at', 'type', 'participantId'].includes(k))))

interface ReviewTableProps {
  rows: readonly StudyReviewRow[]
  /** The PID whose timeline is open. */
  open: string | null
  /** That timeline, or `null` while it loads. */
  events: StudyAuditEvent[] | null
  onRow: (prolificPid: string) => void
  /** Offered under an open, finished, not yet withdrawn row. */
  onWithdraw?: (prolificPid: string) => void
}

/** The outcome cell: a withdrawn participant still shows how they ended. */
const outcomeText = (row: StudyReviewRow): string => (row.withdrawnAt ? `${row.status} · withdrawn` : row.status)

export function ReviewTable({ rows, open, events, onRow, onWithdraw }: ReviewTableProps) {
  return (
    <table className="admin-table study-participants">
      <thead>
        <tr>
          {REVIEW_HEADINGS.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.prolificPid}>
            <tr
              className="study-row"
              data-status={r.status}
              data-withdrawn={r.withdrawnAt ? 'yes' : undefined}
              onClick={() => onRow(r.prolificPid)}
            >
              <td><code>{r.prolificPid}</code></td>
              <td><code>{r.prolificSessionId || '—'}</code></td>
              <td>{outcomeText(r)}</td>
              <td>{r.highestStage}</td>
              <td><code>{r.completionCode ?? '—'}</code></td>
              <td>{stamp(r.startedAt)}</td>
              <td>{r.finishedAt ? stamp(r.finishedAt) : 'in progress'}</td>
              <td>{reviewDuration(r.timeInStudyMs)}{r.finishedAt ? '' : ' so far'}</td>
              <td>{reviewDuration(r.waitedMs)}</td>
              <td>{r.test ? 'test' : '—'}</td>
            </tr>
            {open === r.prolificPid && (
              <tr>
                <td colSpan={REVIEW_HEADINGS.length}>
                  {r.withdrawnAt && (
                    <p className="hint">Withdrawn {stamp(r.withdrawnAt)}. Recordings, survey and event trail deleted.</p>
                  )}
                  {onWithdraw && !r.withdrawnAt && r.status !== 'active' && (
                    <p>
                      <button
                        type="button"
                        className="ghost"
                        data-testid="study-withdraw"
                        onClick={(event) => {
                          event.stopPropagation()
                          onWithdraw(r.prolificPid)
                        }}
                      >
                        Withdraw and delete data
                      </button>
                    </p>
                  )}
                  {events === null ? (
                    <p className="hint">Loading…</p>
                  ) : (
                    <ol className="study-timeline">
                      {events.map((e, i) => (
                        <li key={i}>
                          <code>{e.at}</code> {e.type} <span className="hint">{detail(e)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}
